import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, ShieldCheck, Truck, Star } from "lucide-react";
import Reveal from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import { PRODUCTS, BENEFITS, HERO_IMG, STORY_IMG, SITE } from "@/data/site";

const heroLine = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const TRUST = [
  { icon: Leaf, label: "100% Organic Apples" },
  { icon: ShieldCheck, label: "Raw & Unfiltered" },
  { icon: Truck, label: "Ships Nationwide" },
  { icon: Star, label: "Small-Batch Crafted" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-cream pt-28 md:pt-32">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 pb-16 md:px-8 md:pb-24 lg:grid-cols-2 lg:gap-8">
          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="mb-6 inline-flex items-center gap-2 rounded-full border border-line bg-sand px-4 py-1.5 text-xs font-medium uppercase tracking-[0.15em] text-sage"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-sage" /> Wellness, the natural way
            </motion.div>

            <h1 className="font-heading text-[13vw] font-medium leading-[0.95] tracking-tight text-forest sm:text-6xl lg:text-7xl">
              {["Nature,", "bottled with", "intention."].map((line, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span custom={i} variants={heroLine} initial="hidden" animate="show" className="inline-block">
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-7 max-w-md text-base leading-relaxed text-ink/65 md:text-lg"
            >
              {SITE.legal} crafts raw, unfiltered apple cider vinegar and wellness essentials —
              slow-fermented from organically grown apples, exactly as nature intended.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/products"
                data-testid="hero-shop-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-sage px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-forest"
              >
                Shop the Collection
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
              </Link>
              <Link
                to="/benefits"
                data-testid="hero-benefits-btn"
                className="inline-flex items-center gap-2 rounded-full border border-sage px-7 py-3.5 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage hover:text-white"
              >
                Explore Benefits
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="overflow-hidden rounded-[2rem] border border-line">
              <img src={HERO_IMG} alt="Apple cider vinegar wellness ritual" className="h-full w-full object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-2 hidden rounded-2xl border border-line bg-cream px-5 py-4 shadow-sm sm:block md:-left-6">
              <p className="font-heading text-2xl text-forest">With the Mother</p>
              <p className="text-xs text-ink/55">Live enzymes in every bottle</p>
            </div>
          </motion.div>
        </div>

        {/* trust strip */}
        <div className="border-y border-line bg-sand">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-6 px-5 py-6 md:grid-cols-4 md:px-8">
            {TRUST.map((t) => (
              <div key={t.label} className="flex items-center gap-3">
                <t.icon className="h-5 w-5 text-sage" strokeWidth={1.6} />
                <span className="text-sm font-medium text-ink/75">{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28" data-testid="home-featured">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage">Our Collection</p>
            <h2 className="mt-3 max-w-xl font-heading text-4xl font-medium leading-[1.05] tracking-tight text-forest md:text-5xl">
              Small-batch tonics for everyday rituals
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/products"
              data-testid="home-view-all"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-forest"
            >
              View all products
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.slice(0, 3).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <ProductCard product={p} index={i} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* BENEFITS PREVIEW */}
      <section className="bg-forest py-20 text-cream md:py-28" data-testid="home-benefits">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber">Why apple cider vinegar</p>
            <h2 className="mt-3 max-w-2xl font-heading text-4xl font-medium leading-[1.05] tracking-tight md:text-5xl">
              A daily ritual your body will thank you for
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.slice(0, 6).map((b, i) => (
              <Reveal key={b.title} delay={i * 0.06}>
                <div className="border-t border-cream/20 pt-6">
                  <b.icon className="h-7 w-7 text-amber" strokeWidth={1.5} />
                  <h3 className="mt-4 font-heading text-2xl">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-cream/70">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STORY TEASER */}
      <section className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28" data-testid="home-story">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] border border-line">
              <img src={STORY_IMG} alt="Basket of organic apples" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage">Our Story</p>
            <h2 className="mt-3 font-heading text-4xl font-medium leading-[1.05] tracking-tight text-forest md:text-5xl">
              Rooted in real ingredients & honest craft
            </h2>
            <p className="mt-6 text-base leading-relaxed text-ink/65">
              Medina Lia began with a simple belief: wellness should be pure, honest and accessible. We
              partner with organic orchards and let time do the work — no concentrates, no shortcuts, no
              additives. Just apples, patience and care.
            </p>
            <Link
              to="/about"
              data-testid="home-story-link"
              className="group mt-8 inline-flex items-center gap-2 rounded-full border border-sage px-7 py-3.5 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage hover:text-white"
            >
              Read our story
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 pb-20 md:px-8 md:pb-28">
        <Reveal>
          <div className="mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-terracotta px-8 py-16 text-center md:py-24">
            <h2 className="mx-auto max-w-2xl font-heading text-4xl font-medium leading-[1.05] tracking-tight text-cream md:text-5xl">
              Begin your wellness ritual today
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base leading-relaxed text-cream/85">
              Discover the collection or reach out — we'd love to help you find your perfect blend.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link
                to="/products"
                data-testid="cta-shop-btn"
                className="rounded-full bg-cream px-8 py-3.5 text-sm font-semibold text-forest transition-transform duration-300 hover:scale-[1.03]"
              >
                Shop Now
              </Link>
              <Link
                to="/contact"
                data-testid="cta-contact-btn"
                className="rounded-full border border-cream px-8 py-3.5 text-sm font-semibold text-cream transition-colors duration-300 hover:bg-cream hover:text-forest"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
